/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from 'react';
import Navbar from '../../../common/Navbar/Navbar';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { message, Button, Result } from 'antd';
import { getISTTripDetailsMetadata, getTripDetails } from '../../../api/tripDetails';
import Loader from '../../../common/Loader';
import { formatDateTime } from '../../../utils/utils';
import { StylesProps } from 'src/library/types';
import { getRetailConsignmentStatus, tripStatusPrettyNames } from '../../../utils/consignmentUtils';
import DocumentTitle from 'react-document-title';
import moment, { Moment } from 'moment';
import lodash from 'lodash';
import HeaderCard from './HeaderCard';
import TabSection from './TabSection';
import { vehicleRequestsMMRoute } from 'src/routing/routeConstants';
import { ISTDetailsStyles } from './ISTDetails.styles';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';

interface IProps extends StylesProps<ReturnType<typeof ISTDetailsStyles>> {
  t: any;
  classes: any;
  match: any;
  history: any;
  location: any;
}

const ISTDetailsPage = (props: IProps): React.ReactElement => {
  const { t, classes, match, history, location } = props;

  const [error, setError] = useState<string | false>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [metadataConfig, setMetadataConfig] = useState<any>({
    tabs_to_show: [],
    actions_to_show: [],
    headers_to_show: [],
  });
  const [headerList, setHeaderList] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [taskType, setTaskType] = useState<string | undefined>(undefined);
  const [currentActiveTab, setCurrentActiveTab] = useState<string>('');
  const [data, setData] = useState<any>({
    tripDetails: {
      ist_id: undefined,
      ist_reference_number: null,
      created_at: undefined,
      type: undefined,
      vehicle_number: undefined,
      status: undefined,
      updated_at: undefined,
      start_km_reading: undefined,
      end_km_reading: undefined,
      organisation_id: undefined,
      route_code: '',
      ist_type: null,
      vehicle_make_code: undefined,
      ewb_status: undefined,
      vehicle_make_id: undefined,
      volume_capacity: undefined,
      weight_capacity: undefined,
      transfer_weight: undefined,
      events: [],
    },
    consignmentDetails: {
      complete: [],
    },
  });
  const [fullHeadersList, setFullHeadersList] = useState<any[]>([]);

  const isFloat = (n: number): boolean => {
    return Number(n) === n && n % 1 !== 0;
  };

  const isValidVal = (val: any): any => {
    return Number.isInteger(val) || isFloat(val) ? val : 'Not Available';
  };

  const prepareUpdates = (tabs: any[], activeTab: string): any => {
    const updates: { [key: string]: any } = {};
    tabs.forEach((tab) => {
      updates[tab.tab_header] = tab.keys_to_show;
    });
    return updates[activeTab];
  };

  const prepareHeaderList = (headers: any[], tabs?: any[]): any[] => {
    if (!tabs) return headers;
    const tab = tabs.find((x: any) => x.tab_header === 'Trip Details');
    let columns = lodash.get(tab, 'details[0].columns', null);
    if (columns) {
      columns = columns.map((ele: any) => ({ id: ele.id, show_on_main_dashboard: false }));
    } else {
      columns = [];
    }
    headers = headers.map((ele: any) => ({ id: ele.id, show_on_main_dashboard: true }));
    return [...headers, ...columns];
  };

  const prepareFullHeadersList = (headers: any[], tabs?: any[]): any[] => {
    if (!tabs) return headers;
    const tab = tabs.find((x: any) => x.tab_header === 'Trip Details');
    const columns = lodash.get(tab, 'details[0].columns', null);
    if (!columns) return headers;
    const toRet = [...headers, ...columns];
    return toRet.map((x: any) => ({ ...x, label: x.pretty_name }));
  };

  const fetchDefaultMetadata = async (): Promise<void> => {
    const response = await getISTTripDetailsMetadata();
    if (!response.isSuccess) {
      message.error(response.errorMessage);
      return;
    }
    const tripDetailsMetadata = response?.data?.default_trip_details_metadata;
    const activeTab = 'Waybill List';
    const tabs = tripDetailsMetadata?.tabs_to_show || [];
    const headers = tripDetailsMetadata?.headers_to_show || [];
    const preparedHeaderList = prepareHeaderList(headers, tabs) || [];
    const preparedFullHeadersList = prepareFullHeadersList(headers, tabs);
    setCurrentActiveTab(activeTab);
    setHeaderList(preparedHeaderList);
    setMetadataConfig(tripDetailsMetadata);
    setFullHeadersList(preparedFullHeadersList);
  };

  const fetchTrip = async (): Promise<void> => {
    setIsLoading(true);
    setError(false);
    const response = await getTripDetails({
      tripReferenceNumber: match.params.tripId,
      type: 'ist',
    });
    if (!response.isSuccess) {
      setIsLoading(false);
      setError(response.errorMessage);
      return;
    }
    const res = response.data;
    const ds = res.consignmentDetails.complete;
    res.tripDetails.extra_details = res.tripDetails.extra_details || {};
    const getTaskType = ds.filter((record: any) => record.task_type === 'pickup');
    setDataSource(ds);
    setData(res);
    setTaskType(getTaskType.length ? 'Tasks' : 'Waybill List');
    setIsLoading(false);
  };

  const redirectToVehicleRequests = (): void => {
    history.push(vehicleRequestsMMRoute);
  };

  const handleCurrentActiveTab = (tab: string): void => {
    setCurrentActiveTab(tab);
  };

  const generateDataColumnsTracking = (): any[] => {
    const columnData: any[] = [];
    const tabHeader = metadataConfig?.tabs_to_show?.find(
      (tab: any) => tab.tab_header === 'Trip Tracking',
    );
    const indexList = lodash.get(tabHeader, 'details[0].columns', []);
    indexList.forEach((item: any) => {
      const itemToPush: any = {};
      itemToPush['title'] = t(item.pretty_name);
      itemToPush['dataIndex'] = item.id;
      itemToPush['key'] = item.id;
      itemToPush['width'] = 60;
      if (item.id === 'event_time') {
        itemToPush['render'] = (x: any) => (x ? moment(x).format('DD/MM/YYYY, HH:mm') : null);
      }
      if (item.id === 'type') {
        itemToPush['render'] = (x: any, record: any) => record['event_string'] ?? '';
      }
      columnData.push(itemToPush);
    });
    return columnData;
  };

  const generateDataColumns = (): any => {
    const columnData: any[] = [];
    const tabHeader = metadataConfig?.tabs_to_show?.find(
      (tab: any) => tab.tab_header === 'Waybill List',
    );
    const indexList = lodash.get(tabHeader, 'details[0].columns', []);
    indexList.forEach((item: any) => {
      const itemToPush: any = {};
      itemToPush['title'] = t(item.pretty_name);
      itemToPush['dataIndex'] = item.id;
      itemToPush['width'] = 100;
      itemToPush['key'] = item.id;

      if (item.id === 's_no') {
        itemToPush['render'] = (x: any, record: any, index: number) => <div>{index + 1}</div>;
      }
      if (item.id === 'status') {
        itemToPush['render'] = (x: any, record: any) => (
          <div style={{ minWidth: 140 }}>
            <span>{getRetailConsignmentStatus(x)}</span>
            <div>
              {record.last_main_event_time ? formatDateTime(record.last_main_event_time) : null}
            </div>
          </div>
        );
      }
      if (item.id === 'reference_number') {
        itemToPush['title'] = t(taskType === 'Tasks' ? 'Task #' : item.pretty_name);
        itemToPush['render'] = (x: any) => x;
      }
      if (item.id === 'weight') {
        itemToPush['width'] = 100;
        itemToPush['render'] = (x: any) => (isValidVal(x) ? <span>{x}</span> : null);
      }
      if (item.id === 'updated_at') {
        itemToPush['render'] = (x: any) =>
          (x ? <span>{moment(x).format('YYYY-MM-DD, HH:mm')}</span> : null);
      }
      columnData.push(itemToPush);
    });
    return columnData;
  };

  const getScrollWidth = (): number => {
    const columns = generateDataColumns();
    return columns.reduce((acc, cur) => acc + (cur.width || 150), 0);
  };

  const renderEWBStatus = (x: string): JSX.Element => {
    return x === 'success' ? (
      <span>
        <CheckCircleFilled style={{ color: '#008000' }} className={classes.leftSpace} />
        {t('Success')}
      </span>
    ) : (
      <span>
        <CloseCircleFilled style={{ color: '#EC544A' }} className={classes.leftSpace} />
        {t('Failed')}
      </span>
    );
  };

  const renderUtilisationData = (value: any): JSX.Element => <div>{value || '-'}</div>;

  const getPropsByHeader = (tabHeader: string): any => {
    const consignmentProps = {
      rowKey: (record: any) => record['reference_number'],
      scroll: { x: getScrollWidth() },
      dataSource,
      columns: generateDataColumns(),
      rowClassName: classes.green,
    };

    const tripTrackingProps = {
      pagination: false,
      dataSource: data.tripDetails?.events || [],
      columns: generateDataColumnsTracking(),
    };

    switch (tabHeader) {
      case 'Waybill List':
        return consignmentProps;
      case 'Trip Tracking':
        return tripTrackingProps;
      default:
        return {};
    }
  };

  const isBooleanVal = (val: any): JSX.Element => (val ? <div>TRUE</div> : <div>FALSE</div>);

  const getCustomDetailsCard = (
    column: any,
    dataObj: any,
  ): { hint: string; content: React.ReactNode } => {
    let dataToRender = {
      hint: '',
      content: <></>,
    };
    switch (column.id) {
      case 'status':
        dataToRender = {
          hint: column.pretty_name,
          content: tripStatusPrettyNames(dataObj[column.id]),
        };
        break;
      case 'created_at':
      case 'started_at':
      case 'updated_at':
      case 'last_status_change_time':
        dataToRender = {
          hint: column.pretty_name,
          content: <div>{formatDateTime(dataObj[column.id])}</div>,
        };
        break;
      case 'start_km_reading':
        dataToRender = {
          hint: column.pretty_name,
          content: <div>{dataObj[column.id] || 'Not Available'}</div>,
        };
        break;
      case 'end_km_reading':
        dataToRender = {
          hint: column.pretty_name,
          content: <div>{dataObj[column.id] || 'Not Available'}</div>,
        };
        break;
      case 'ewb_status':
        dataToRender = {
          hint: column.pretty_name,
          content: renderEWBStatus(dataObj[column.id]),
        };
        break;
      case 'transfer_weight':
      case 'volume_capacity':
      case 'weight_capacity':
        dataToRender = {
          hint: column.pretty_name,
          content: renderUtilisationData(dataObj[column.id]),
        };
        break;
      case 'ist_type':
      case 'vehicle_make':
        dataToRender = {
          hint: column.pretty_name,
          content: dataObj[column.id] ? (
            <div style={{ textTransform: 'capitalize' }}>{dataObj[column.id]}</div>
          ) : null,
        };
        break;
      default:
        if (column.type === 'boolean') {
          dataToRender = {
            hint: column.pretty_name,
            content: isBooleanVal(dataObj[column.id]),
          };
        } else {
          dataToRender = {
            hint: column.pretty_name,
            content: dataObj[column.id],
          };
        }
        break;
    }
    return dataToRender;
  };

  useEffect(() => {
    fetchDefaultMetadata();
    fetchTrip();
  }, []);

  useEffect(() => {
    fetchTrip();
  }, [match.params.tripId]);

  if (error) {
    return (
      <div>
        <DocumentTitle title={t('Trip Details')} />
        <Navbar pageTitle={t('Trip Details')} />
        <Result
          status="error"
          title="Error"
          subTitle={error}
          extra={[
            <Button type="primary" onClick={redirectToVehicleRequests} key="redirect">
              {t('Go To Vehicle Requests')}
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      <DocumentTitle title={t('Trip Manager Details')} />
      <Navbar pageTitle={t('Trip Manager Details')} />
      {isLoading ? (
        <Loader zIndex={100} />
      ) : (
        <div style={{ maxHeight: '100vh', overflowX: 'hidden' }}>
          <HeaderCard viewSource="trip details" fullHeadersList={fullHeadersList} columns={headerList} data={data.tripDetails} />
          <div className={classes.tableContainer} style={{ display: 'flex' }}>
            <div style={{ width: '100%', overflowX: 'scroll' }}>
              <TabSection
                tabs={metadataConfig?.tabs_to_show}
                data={data}
                currentActiveTab={currentActiveTab}
                handleCurrentActiveTab={handleCurrentActiveTab}
                getPropsByHeader={getPropsByHeader}
                getCustomDetailsCard={getCustomDetailsCard}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const hocConfig: HocOptions = {
  connectJss: {
    useJss: true,
    styleSheet: ISTDetailsStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(ISTDetailsPage);
